import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import PageIndicator from '@/components/PageIndicator';
import FormField from '@/components/FormField';
import { generateAndSavePagePDF, generateFormId } from '@/utils/pdfGenerator';
import { SOUTH_AFRICAN_BANKS, validateAccountNumber } from '@/utils/bankData';
import html2pdf from 'html2pdf.js';

// Local image paths to avoid CORS issues with html2canvas PDF generation
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const REFERRAL_OPTIONS = [
  'Word-of-Mouth',
  'Our Website',
  "Xero's Website (List of Xero Partners)",
  'Google Search',
  'Brochure Handout',
  'Board Sign',
  'Advert Sign',
  'LinkedIn',
  'Facebook'
];

const PageOne = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const previousData = location.state || {};
  
  // Generate or use existing formId for PDF storage
  const [formId] = useState(() => previousData.formId || generateFormId());
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  const [clientType, setClientType] = useState(previousData.clientType || '');
  const [formData, setFormData] = useState({
    firstName: previousData.firstName || '',
    secondName: previousData.secondName || '',
    middleName: previousData.middleName || '',
    surname: previousData.surname || '',
    idNumber: previousData.idNumber || '',
    taxNumber: previousData.taxNumber || '',
    emailAddress: previousData.emailAddress || '',
    workEmail: previousData.workEmail || '',
    cellphoneNumber: previousData.cellphoneNumber || '',
    physicalAddress: previousData.physicalAddress || '',
    referralSource: previousData.referralSource || '',
    maritalStatus: previousData.maritalStatus || '',
    page1BankName: previousData.page1BankName || '',
    page1BranchName: previousData.page1BranchName || '',
    page1BranchCode: previousData.page1BranchCode || '',
    page1AccountNumber: previousData.page1AccountNumber || '',
    page1AccountType: previousData.page1AccountType || 'Current'
  });
  const [openBankCombobox, setOpenBankCombobox] = useState(false);
  const [page1AccountWarning, setPage1AccountWarning] = useState('');
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });
  const [fieldErrors, setFieldErrors] = useState({
    emailAddress: '',
    workEmail: '',
    idNumber: '',
    taxNumber: '',
    cellphoneNumber: ''
  });
  const [touchedFields, setTouchedFields] = useState({
    emailAddress: false,
    workEmail: false,
    idNumber: false,
    taxNumber: false,
    cellphoneNumber: false
  });
  const [idNumberInfo, setIdNumberInfo] = useState(null);
  const [showAddressTip, setShowAddressTip] = useState(false);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Validate ID number on initial load if pre-populated (coming back from Page 2)
  useEffect(() => {
    if (previousData.idNumber) {
      const result = validateSAIdNumber(previousData.idNumber);
      if (result.valid) {
        setIdNumberInfo(result.info);
      }
    }
  }, []);

  // Email validation regex - checks for valid email format
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Validate Personal Income Tax Number
  const validateTaxNumber = (taxNumber) => {
    if (!taxNumber || taxNumber.trim() === '') {
      return { valid: false, error: '' }; // Empty handled by required field
    }

    // Remove any spaces
    const cleanTax = taxNumber.replace(/\s/g, '');

    // Check: Must be exactly 10 digits, no special characters, no letters
    if (!/^\d{10}$/.test(cleanTax)) {
      if (/[a-zA-Z]/.test(taxNumber)) {
        return { 
          valid: false, 
          error: 'Tax number must not contain letters'
        };
      }
      if (/[^0-9\s]/.test(taxNumber)) {
        return { 
          valid: false, 
          error: 'Tax number must not contain special characters'
        };
      }
      if (cleanTax.length !== 10) {
        return { 
          valid: false, 
          error: `Tax number must be exactly 10 digits. Currently ${cleanTax.length} digits.`
        };
      }
    }

    // Check: Tax number cannot start with 4, 7, or 9
    const firstDigit = cleanTax[0];
    if (firstDigit === '4' || firstDigit === '7' || firstDigit === '9') {
      return { 
        valid: false, 
        error: 'Invalid tax number, please double check'
      };
    }

    // Check: Prevent all same digits (e.g., 1111111111, 0000000000)
    if (/^(\d)\1{9}$/.test(cleanTax)) {
      return { 
        valid: false, 
        error: 'Invalid tax number - cannot be all the same digit'
      };
    }

    // Check: Prevent sequential ascending numbers (e.g., 0123456789, 1234567890)
    const ascendingSequences = ['0123456789', '1234567890', '2345678901', '3456789012', '4567890123', '5678901234', '6789012345', '7890123456', '8901234567', '9012345678'];
    if (ascendingSequences.includes(cleanTax)) {
      return { 
        valid: false, 
        error: 'Invalid tax number - sequential numbers not allowed'
      };
    }

    // Check: Prevent sequential descending numbers (e.g., 9876543210, 0987654321)
    const descendingSequences = ['9876543210', '0987654321', '1098765432', '2109876543', '3210987654', '4321098765', '5432109876', '6543210987', '7654321098', '8765432109'];
    if (descendingSequences.includes(cleanTax)) {
      return { 
        valid: false, 
        error: 'Invalid tax number - sequential numbers not allowed'
      };
    }

    // Check: Prevent repeating patterns (e.g., 1212121212, 1231231231, 1234123412)
    // 2-digit repeating pattern
    if (/^(\d{2})\1{4}$/.test(cleanTax)) {
      return { 
        valid: false, 
        error: 'Invalid tax number - repeating pattern not allowed'
      };
    }
    // 5-digit repeating pattern
    if (/^(\d{5})\1$/.test(cleanTax)) {
      return { 
        valid: false, 
        error: 'Invalid tax number - repeating pattern not allowed'
      };
    }

    // Check: Prevent common bypass attempts
    const invalidNumbers = [
      '1234567890', '0987654321', '1111111111', '0000000000',
      '1234512345', '9999999999', '1010101010', '1122334455',
      '1212121212', '1231231231', '1234123412', '5555555555',
      '1357924680', '2468013579', '9876509876', '0123401234'
    ];
    if (invalidNumbers.includes(cleanTax)) {
      return { 
        valid: false, 
        error: 'Invalid tax number, please enter your actual tax number'
      };
    }

    // Check: Prevent any 2-digit pattern repeating 3 or more times
    const twoDigitPatterns = {};
    for (let i = 0; i <= cleanTax.length - 2; i++) {
      const pattern = cleanTax.substring(i, i + 2);
      twoDigitPatterns[pattern] = (twoDigitPatterns[pattern] || 0) + 1;
      
      if (twoDigitPatterns[pattern] >= 3) {
        return { 
          valid: false, 
          error: 'Invalid tax number - suspicious repeating pattern detected'
        };
      }
    }

    // Check: Prevent any 3-digit pattern repeating 3 or more times
    const threeDigitPatterns = {};
    for (let i = 0; i <= cleanTax.length - 3; i++) {
      const pattern = cleanTax.substring(i, i + 3);
      threeDigitPatterns[pattern] = (threeDigitPatterns[pattern] || 0) + 1;
      
      if (threeDigitPatterns[pattern] >= 3) {
        return { 
          valid: false, 
          error: 'Invalid tax number - suspicious repeating pattern detected'
        };
      }
    }

    // Check: Prevent any 4-digit pattern repeating 2 or more times
    const fourDigitPatterns = {};
    for (let i = 0; i <= cleanTax.length - 4; i++) {
      const pattern = cleanTax.substring(i, i + 4);
      fourDigitPatterns[pattern] = (fourDigitPatterns[pattern] || 0) + 1;
      
      if (fourDigitPatterns[pattern] >= 2) {
        return { 
          valid: false, 
          error: 'Invalid tax number - suspicious repeating pattern detected'
        };
      }
    }

    // Check: Prevent any 5-digit pattern repeating 2 or more times
    const fiveDigitPatterns = {};
    for (let i = 0; i <= cleanTax.length - 5; i++) {
      const pattern = cleanTax.substring(i, i + 5);
      fiveDigitPatterns[pattern] = (fiveDigitPatterns[pattern] || 0) + 1;
      
      if (fiveDigitPatterns[pattern] >= 2) {
        return { 
          valid: false, 
          error: 'Invalid tax number - suspicious repeating pattern detected'
        };
      }
    }

    // Check: Prevent 4+ consecutive ascending or descending digits
    for (let i = 0; i <= cleanTax.length - 4; i++) {
      let isAscending = true;
      let isDescending = true;
      
      // Check if next 4 digits are consecutive ascending
      for (let j = i; j < i + 3; j++) {
        const current = parseInt(cleanTax[j]);
        const next = parseInt(cleanTax[j + 1]);
        
        if (next !== current + 1) {
          isAscending = false;
        }
        if (next !== current - 1) {
          isDescending = false;
        }
      }
      
      if (isAscending || isDescending) {
        return { 
          valid: false, 
          error: 'Invalid tax number - sequential numbers not allowed'
        };
      }
    }

    return { valid: true, error: '' };
  };

  // Valid South African mobile prefixes
  const validSAMobilePrefixes = [
    '060', '061', '062', '063', '064', '065', '066', '067', '068', '069',
    '071', '072', '073', '074', '076', '078', '079',
    '081', '082', '083', '084'
  ];

  // Format cellphone number as XXX XXX XXXX
  const formatCellphoneNumber = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limited = digits.slice(0, 10);
    
    // Format as XXX XXX XXXX
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `${limited.slice(0, 3)} ${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6)}`;
    }
  };

  // Validate South African cellphone number
  const validateSACellphone = (cellphone) => {
    if (!cellphone || cellphone.trim() === '') {
      return { valid: false, error: '' }; // Empty handled by required field
    }

    // Remove spaces to get just digits
    const digits = cellphone.replace(/\s/g, '');

    // Check 1: Must be exactly 10 digits
    if (digits.length !== 10) {
      return { 
        valid: false, 
        error: `Cellphone number must be exactly 10 digits. Currently ${digits.length} digits.`
      };
    }

    // Check 2: Must contain only digits
    if (!/^\d+$/.test(digits)) {
      return { 
        valid: false, 
        error: 'Cellphone number must contain only numbers'
      };
    }

    // Check 3: Must start with 0
    if (digits[0] !== '0') {
      return { 
        valid: false, 
        error: 'Cellphone number must start with 0'
      };
    }

    // Check 4: First three digits must be a valid SA mobile prefix
    const prefix = digits.slice(0, 3);
    if (!validSAMobilePrefixes.includes(prefix)) {
      return { 
        valid: false, 
        error: 'Invalid cellphone number, please double check'
      };
    }

    return { valid: true, error: '' };
  };

  // South African ID Number Validation
  const validateSAIdNumber = (idNumber) => {
    if (!idNumber || idNumber.trim() === '') {
      return { valid: false, error: '', info: null }; // Empty handled by required field
    }

    // Remove any spaces or dashes
    const cleanId = idNumber.replace(/[\s-]/g, '');

    // Check 1: Must be exactly 13 digits
    if (!/^\d{13}$/.test(cleanId)) {
      return { 
        valid: false, 
        error: 'ID number must be exactly 13 digits (no letters or special characters)',
        info: null 
      };
    }

    // Extract components
    const year = cleanId.substring(0, 2);
    const month = cleanId.substring(2, 4);
    const day = cleanId.substring(4, 6);
    const genderDigits = cleanId.substring(6, 10);
    const citizenshipDigit = cleanId.substring(10, 11);
    const raceDigit = cleanId.substring(11, 12);
    const checkDigit = cleanId.substring(12, 13);

    // Check 2: Validate date of birth (YYMMDD)
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    const yearNum = parseInt(year, 10);

    // Month must be 01-12
    if (monthNum < 1 || monthNum > 12) {
      return { 
        valid: false, 
        error: `Invalid month in ID number: ${month}. Month must be between 01 and 12`,
        info: null 
      };
    }

    // Determine full year (assuming 00-current year+10 is 2000s, rest is 1900s)
    const currentYear = new Date().getFullYear();
    const currentYearLastTwo = currentYear % 100;
    const fullYear = yearNum <= currentYearLastTwo + 10 ? 2000 + yearNum : 1900 + yearNum;

    // Check if it's a leap year
    const isLeapYear = (fullYear % 4 === 0 && fullYear % 100 !== 0) || (fullYear % 400 === 0);

    // Days per month
    const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const maxDays = daysInMonth[monthNum - 1];

    if (dayNum < 1 || dayNum > maxDays) {
      return { 
        valid: false, 
        error: `Invalid day in ID number: ${day}. Day must be between 01 and ${maxDays} for month ${month}`,
        info: null 
      };
    }

    // Check if birth date is not in the future
    const birthDate = new Date(fullYear, monthNum - 1, dayNum);
    const today = new Date();
    if (birthDate > today) {
      return { 
        valid: false, 
        error: 'Date of birth cannot be in the future',
        info: null 
      };
    }

    // Check 3: Gender sequence (0000-4999 = Female, 5000-9999 = Male)
    const genderNum = parseInt(genderDigits, 10);
    const gender = genderNum < 5000 ? 'Female' : 'Male';

    // Check 4: Citizenship digit (0 = SA Citizen, 1 = Permanent Resident, 2 = Refugee)
    let citizenship = '';
    if (citizenshipDigit === '0') {
      citizenship = 'South African Citizen';
    } else if (citizenshipDigit === '1') {
      citizenship = 'Permanent Resident';
    } else if (citizenshipDigit === '2') {
      citizenship = 'Refugee';
    } else {
      return { 
        valid: false, 
        error: `Invalid citizenship digit: ${citizenshipDigit}. Must be 0 (SA Citizen), 1 (Permanent Resident), or 2 (Refugee)`,
        info: null 
      };
    }

    // Check 5: Race/obsolete digit (should be 8 or 9 in modern IDs)
    // Note: We don't reject based on this as older IDs may have different values
    const isModernId = raceDigit === '8' || raceDigit === '9';

    // Check 6: Luhn Algorithm (Modulus 10) for check digit validation
    const luhnValid = validateLuhnChecksum(cleanId);
    if (!luhnValid) {
      return { 
        valid: false, 
        error: 'Invalid ID number: Check digit validation failed',
        info: null 
      };
    }

    // Calculate age
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    // All validations passed
    return {
      valid: true,
      error: '',
      info: {
        dateOfBirth: birthDate.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }),
        age: age,
        gender: gender,
        citizenship: citizenship,
        isModernId: isModernId
      }
    };
  };

  // Luhn Algorithm implementation
  const validateLuhnChecksum = (idNumber) => {
    let sum = 0;
    let isSecond = false;

    // Process from right to left
    for (let i = idNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(idNumber[i], 10);

      if (isSecond) {
        digit *= 2;
        if (digit > 9) {
          digit = Math.floor(digit / 10) + (digit % 10);
        }
      }

      sum += digit;
      isSecond = !isSecond;
    }

    return sum % 10 === 0;
  };

  const validateEmail = (email, fieldName) => {
    if (!email || email.trim() === '') {
      return ''; // Empty is handled by required field validation
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address (e.g., name@example.com)';
    }
    return '';
  };

  // Validate that work email is different from personal email
  const validateWorkEmail = (workEmail) => {
    if (!workEmail || workEmail.trim() === '') {
      return ''; // Empty is handled by required field validation
    }
    if (!emailRegex.test(workEmail)) {
      return 'Please enter a valid email address (e.g., name@example.com)';
    }
    // Check if work email is the same as personal email (case-insensitive)
    if (formData.emailAddress && workEmail.toLowerCase().trim() === formData.emailAddress.toLowerCase().trim()) {
      return 'Work/Alternative email cannot be the same as your personal email address';
    }
    return '';
  };

  // Fields to skip trimming (digits-only, physical address, or already validated)
  const skipTrimFields = ['physicalAddress', 'idNumber', 'taxNumber', 'cellphoneNumber'];
  
  // Handle blur event to trim spaces from input fields
  const handleInputBlur = (field) => {
    if (skipTrimFields.includes(field)) return;
    
    const currentValue = formData[field];
    if (currentValue && typeof currentValue === 'string') {
      // Trim leading/trailing spaces and collapse multiple spaces to single space
      const trimmedValue = currentValue.trim().replace(/\s+/g, ' ');
      if (trimmedValue !== currentValue) {
        setFormData(prev => ({
          ...prev,
          [field]: trimmedValue
        }));
      }
    }
  };

  const handleInputChange = (field, value) => {
    // Special handling for cellphone number - auto-format
    if (field === 'cellphoneNumber') {
      const formattedValue = formatCellphoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [field]: formattedValue
      }));
      
      // Validate on the fly
      const result = validateSACellphone(formattedValue);
      if (result.valid) {
        setFieldErrors(prev => ({ ...prev, cellphoneNumber: '' }));
      }
      return;
    }

    // Special handling for tax number - limit to 10 digits only
    if (field === 'taxNumber') {
      // Remove any non-digit characters and limit to 10 digits
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [field]: digitsOnly
      }));
      
      // Validate on the fly
      const result = validateTaxNumber(digitsOnly);
      if (result.valid) {
        setFieldErrors(prev => ({ ...prev, taxNumber: '' }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (field === 'emailAddress' || field === 'workEmail') {
      if (value && emailRegex.test(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
    }

    // Clear ID error when typing and validate on the fly
    if (field === 'idNumber') {
      const result = validateSAIdNumber(value);
      if (result.valid) {
        setFieldErrors(prev => ({ ...prev, idNumber: '' }));
        setIdNumberInfo(result.info);
      } else {
        setIdNumberInfo(null);
      }
    }
  };

  const handleEmailBlur = (field) => {
    // Trim spaces from email
    const currentValue = formData[field];
    if (currentValue && typeof currentValue === 'string') {
      const trimmedValue = currentValue.trim().replace(/\s+/g, '');
      if (trimmedValue !== currentValue) {
        setFormData(prev => ({
          ...prev,
          [field]: trimmedValue
        }));
      }
    }
    
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
    
    // Use different validation for work email (validate trimmed value)
    const valueToValidate = currentValue ? currentValue.trim().replace(/\s+/g, '') : '';
    const error = field === 'workEmail' 
      ? validateWorkEmail(valueToValidate)
      : validateEmail(valueToValidate, field);
    
    setFieldErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleTaxNumberBlur = () => {
    setTouchedFields(prev => ({
      ...prev,
      taxNumber: true
    }));
    
    const result = validateTaxNumber(formData.taxNumber);
    setFieldErrors(prev => ({
      ...prev,
      taxNumber: result.error
    }));
  };

  const handleCellphoneBlur = () => {
    setTouchedFields(prev => ({
      ...prev,
      cellphoneNumber: true
    }));
    
    const result = validateSACellphone(formData.cellphoneNumber);
    setFieldErrors(prev => ({
      ...prev,
      cellphoneNumber: result.error
    }));
  };

  const handleIdNumberBlur = () => {
    setTouchedFields(prev => ({
      ...prev,
      idNumber: true
    }));
    
    const result = validateSAIdNumber(formData.idNumber);
    setFieldErrors(prev => ({
      ...prev,
      idNumber: result.error
    }));
    setIdNumberInfo(result.valid ? result.info : null);
  };

  const requiredFields = [
    { key: 'firstName', label: 'First Name' },
    { key: 'surname', label: 'Surname' },
    { key: 'idNumber', label: 'ID Number' },
    { key: 'taxNumber', label: 'Personal Income Tax Number' },
    { key: 'emailAddress', label: 'Email Address' },
    { key: 'cellphoneNumber', label: 'Cellphone Number' },
    { key: 'maritalStatus', label: 'Marital Status' },
    { key: 'page1BankName', label: 'Bank Name (Bank Account Details for Tax Refund)' },
    { key: 'page1AccountNumber', label: 'Bank Account Number (Bank Account Details for Tax Refund)' },
    { key: 'physicalAddress', label: 'Physical Address' }
  ];

  const validateForm = () => {
    if (!clientType) {
      setErrorModal({ open: true, message: 'Please select whether you are a New Client or Existing Client.' });
      return false;
    }
    
    for (const field of requiredFields) {
      if (!formData[field.key] || formData[field.key].trim() === '') {
        setErrorModal({ open: true, message: `Please fill in the required field: ${field.label}` });
        return false;
      }
    }

    // ID Number validation
    const idResult = validateSAIdNumber(formData.idNumber);
    if (!idResult.valid && idResult.error) {
      setFieldErrors(prev => ({ ...prev, idNumber: idResult.error }));
      setTouchedFields(prev => ({ ...prev, idNumber: true }));
      setErrorModal({ open: true, message: 'ID Number: ' + idResult.error });
      return false;
    }

    // Tax Number validation
    const taxResult = validateTaxNumber(formData.taxNumber);
    if (!taxResult.valid && taxResult.error) {
      setFieldErrors(prev => ({ ...prev, taxNumber: taxResult.error }));
      setTouchedFields(prev => ({ ...prev, taxNumber: true }));
      setErrorModal({ open: true, message: 'Personal Income Tax Number: ' + taxResult.error });
      return false;
    }

    // Cellphone validation
    const cellResult = validateSACellphone(formData.cellphoneNumber);
    if (!cellResult.valid && cellResult.error) {
      setFieldErrors(prev => ({ ...prev, cellphoneNumber: cellResult.error }));
      setTouchedFields(prev => ({ ...prev, cellphoneNumber: true }));
      setErrorModal({ open: true, message: 'Cellphone Number: ' + cellResult.error });
      return false;
    }

    // Email format validation
    const emailError = validateEmail(formData.emailAddress, 'emailAddress');
    if (emailError) {
      setFieldErrors(prev => ({ ...prev, emailAddress: emailError }));
      setTouchedFields(prev => ({ ...prev, emailAddress: true }));
      setErrorModal({ open: true, message: 'Email Address: ' + emailError });
      return false;
    }

    // Only validate work email if it's provided (it's optional)
    if (formData.workEmail && formData.workEmail.trim()) {
      const workEmailError = validateWorkEmail(formData.workEmail);
      if (workEmailError) {
        setFieldErrors(prev => ({ ...prev, workEmail: workEmailError }));
        setTouchedFields(prev => ({ ...prev, workEmail: true }));
        setErrorModal({ open: true, message: 'Work/Alternative Email Address: ' + workEmailError });
        return false;
      }
    }

    // Bank account number validation (Bank Account Details for Tax Refund)
    if (formData.page1BankName && formData.page1AccountNumber) {
      const acctError = validateAccountNumber(formData.page1AccountNumber, formData.page1BankName);
      if (acctError) {
        setPage1AccountWarning(acctError);
        setErrorModal({ open: true, message: 'Bank Account Number: ' + acctError });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Generate PDF of current page before navigating
    setIsGeneratingPDF(true);
    
    // Create full-screen overlay to hide PDF generation process from user
    const overlay = document.createElement('div');
    overlay.id = 'pdf-generation-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(255, 255, 255, 0.97);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;
    overlay.innerHTML = `
      <div style="text-align: center;">
        <div style="width: 48px; height: 48px; border: 4px solid #e5e7eb; border-top-color: #f97316; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
        <p style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0;">Saving your progress...</p>
        <p style="font-size: 14px; color: #6b7280; margin-top: 8px;">Please wait while we generate your PDF</p>
      </div>
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    `;
    document.body.appendChild(overlay);
    
    try {
      const element = document.querySelector('#page-pdf-content');
      if (!element) {
        console.error('[Page1] Element #page-pdf-content not found!');
        setIsGeneratingPDF(false);
        overlay.remove();
        return;
      }
      
      // === BULLETPROOFING: Wait for fonts to load ===
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise(r => setTimeout(r, 200)); // Additional stabilization delay
      
      // === BULLETPROOFING: Store original body/element state ===
      const originalBodyStyle = document.body.style.cssText;
      const originalElementWidth = element.style.width;
      const originalElementMaxWidth = element.style.maxWidth;
      const originalElementMinWidth = element.style.minWidth;
      const originalElementTransform = element.style.transform;
      const originalElementPosition = element.style.position;
      
      // === BULLETPROOFING: Force fixed viewport width for consistent rendering ===
      // Use 794px (A4 width at 96 DPI) for proper 1:1 scale on PDF
      const PDF_RENDER_WIDTH = 794;
      document.body.style.overflow = 'hidden';
      element.style.width = `${PDF_RENDER_WIDTH}px`;
      element.style.maxWidth = `${PDF_RENDER_WIDTH}px`;
      element.style.minWidth = `${PDF_RENDER_WIDTH}px`;
      element.style.position = 'relative';
      element.style.transform = 'scale(1)'; // Reset any browser scaling
      
      // Store original values for restoration
      const originalStyles = new Map();
      const hiddenElements = [];
      
      const hideElement = (el) => {
        if (el) {
          originalStyles.set(el, el.style.cssText);
          el.style.display = 'none';
          hiddenElements.push(el);
        }
      };
      
      const modifyStyle = (el, newStyle) => {
        if (el) {
          if (!originalStyles.has(el)) {
            originalStyles.set(el, el.style.cssText);
          }
          Object.assign(el.style, newStyle);
        }
      };
      
      // === BULLETPROOFING: Add page-break prevention styles ===
      const styleTag = document.createElement('style');
      styleTag.id = 'pdf-bulletproof-styles';
      styleTag.textContent = `
        #page-pdf-content .grid, 
        #page-pdf-content .flex,
        #page-pdf-content [role="radiogroup"],
        #page-pdf-content .space-y-2,
        #page-pdf-content .space-y-4,
        #page-pdf-content .space-y-6,
        #page-pdf-content .space-y-8 {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
      `;
      document.head.appendChild(styleTag);
      
      // === BULLETPROOFING: Force 2-column grids via JavaScript (overrides Tailwind responsive) ===
      element.querySelectorAll('.grid').forEach(grid => {
        const classList = grid.className;
        if (classList.includes('grid-cols-2') || classList.includes('sm:grid-cols-2') || 
            classList.includes('md:grid-cols-2') || classList.includes('lg:grid-cols-2')) {
          modifyStyle(grid, { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' });
        }
        if (classList.includes('grid-cols-3') || classList.includes('sm:grid-cols-3') || 
            classList.includes('md:grid-cols-3') || classList.includes('lg:grid-cols-3')) {
          modifyStyle(grid, { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' });
        }
        if (classList.includes('grid-cols-4') || classList.includes('sm:grid-cols-4') || 
            classList.includes('md:grid-cols-4') || classList.includes('lg:grid-cols-4')) {
          modifyStyle(grid, { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' });
        }
      });
      
      // === PDF MODIFICATIONS ===
      // Keep the PDF's original top spacing (the page itself has only 2px above the logo; was py-8 sm:py-12, PDF rendered at 794px)
      modifyStyle(element, { paddingTop: '3rem' });
      
      // Add page break class to heading section - break will happen AFTER this element
      const headingSection = element.querySelector('.mb-8.sm\\:mb-10');
      if (headingSection) {
        // Add the page break class that html2pdf will recognize
        headingSection.classList.add('page-break-after-cover');
        modifyStyle(headingSection, { 
          marginBottom: '0px',
          paddingBottom: '20px',
          width: '100%',
          textAlign: 'center',
          pageBreakAfter: 'always',
          breakAfter: 'page'
        });
        
        // Also ensure all children of the heading section are centered
        headingSection.querySelectorAll('*').forEach(child => {
          modifyStyle(child, { 
            marginLeft: 'auto',
            marginRight: 'auto',
            textAlign: 'center'
          });
        });
      }
      
      // Hide "Please select your client status:" label
      element.querySelectorAll('label').forEach(el => {
        if (el.textContent?.includes('Please select your client status')) {
          hideElement(el.closest('.pb-2') || el);
        }
      });
      
      // Compact layout spacing
      element.querySelectorAll('.space-y-8 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '14px' });
      });
      element.querySelectorAll('.space-y-6 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '11px' });
      });
      element.querySelectorAll('.space-y-4 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '10px' });
      });
      element.querySelectorAll('.space-y-2 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '9px' });
      });
      element.querySelectorAll('[role="radiogroup"]').forEach(el => {
        modifyStyle(el, { marginBottom: '9px' });
      });
      element.querySelectorAll('.p-6').forEach(el => {
        modifyStyle(el, { padding: '16px' });
      });
      element.querySelectorAll('.sm\\:p-10').forEach(el => {
        modifyStyle(el, { padding: '16px' });
      });
      element.querySelectorAll('.py-8, .sm\\:py-12').forEach(el => {
        modifyStyle(el, { paddingTop: '12px', paddingBottom: '12px' });
      });
      element.querySelectorAll('.gap-6').forEach(el => {
        modifyStyle(el, { rowGap: '13px', columnGap: '8px' });
      });
      element.querySelectorAll('.gap-4').forEach(el => {
        modifyStyle(el, { rowGap: '11px', columnGap: '8px' });
      });
      element.querySelectorAll('.mb-6, .mb-4, .mb-8').forEach(el => {
        modifyStyle(el, { marginBottom: '9px' });
      });
      element.querySelectorAll('label').forEach(el => {
        if (!el.closest('[role="radiogroup"]')) {
          modifyStyle(el, { marginBottom: '9px' });
        }
      });
      
      // Hide non-selected radio
      const selectedType = clientType;
      element.querySelectorAll('[role="radiogroup"] > div').forEach(item => {
        const label = item.querySelector('label');
        if (label) {
          const text = label.textContent?.toLowerCase() || '';
          if ((selectedType === 'new' && text.includes('existing')) ||
              (selectedType === 'existing' && text.includes('new'))) {
            hideElement(item);
          }
        }
      });
      
      // Top-align all fields
      element.querySelectorAll('input, textarea, button[role="combobox"], select').forEach(field => {
        modifyStyle(field, { verticalAlign: 'top', alignSelf: 'flex-start' });
      });
      element.querySelectorAll('.flex, .flex.items-center, .grid').forEach(el => {
        modifyStyle(el, { alignItems: 'flex-start' });
      });
      element.querySelectorAll('[role="radio"]').forEach(radio => {
        modifyStyle(radio, { marginTop: '3px', alignSelf: 'flex-start' });
      });
      element.querySelectorAll('label').forEach(label => {
        modifyStyle(label, { display: 'inline-flex', alignItems: 'flex-start', verticalAlign: 'top' });
      });
      element.querySelectorAll('[role="radiogroup"] label').forEach(label => {
        modifyStyle(label, { 
          display: 'inline-flex', alignItems: 'flex-start', verticalAlign: 'top',
          paddingTop: '3px', paddingBottom: '12px', minHeight: '38px', lineHeight: '1.3'
        });
      });
      element.querySelectorAll('button span, label span').forEach(span => {
        modifyStyle(span, { verticalAlign: 'top', alignSelf: 'flex-start' });
      });
      element.querySelectorAll('.space-y-2, .space-y-4, .space-y-6, .space-y-8').forEach(el => {
        modifyStyle(el, { alignItems: 'flex-start' });
      });
      
      // Fix dropdown display
      element.querySelectorAll('button[role="combobox"]').forEach(trigger => {
        modifyStyle(trigger, { 
          alignItems: 'flex-start', justifyContent: 'flex-start',
          paddingTop: '4px', paddingBottom: '10px', height: 'auto', minHeight: '44px'
        });
        const span = trigger.querySelector('span');
        if (span) {
          modifyStyle(span, { 
            whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset',
            lineHeight: '1.5', alignSelf: 'flex-start', marginTop: '0px'
          });
        }
      });
      
      // Hide validation messages and tooltips
      element.querySelectorAll('.bg-green-50').forEach(el => hideElement(el));
      element.querySelectorAll('[data-hide-in-pdf="true"]').forEach(el => hideElement(el));
      element.querySelectorAll('.bg-blue-50').forEach(el => hideElement(el));
      
      // Clear work email field placeholder if empty (hide placeholder text in PDF)
      const workEmailInput = element.querySelector('input[placeholder*="work.email"]');
      let originalWorkEmailPlaceholder = '';
      if (workEmailInput && !formData.workEmail) {
        originalWorkEmailPlaceholder = workEmailInput.placeholder;
        workEmailInput.placeholder = '';
      }
      
      // Clear Second Name field placeholder if empty (hide placeholder text in PDF)
      const secondNameInput = element.querySelector('input[placeholder*="second name"]');
      let originalSecondNamePlaceholder = '';
      if (secondNameInput && !formData.secondName) {
        originalSecondNamePlaceholder = secondNameInput.placeholder;
        secondNameInput.placeholder = '';
      }
      
      // Clear Middle Name field placeholder if empty (hide placeholder text in PDF)
      const middleNameInput = element.querySelector('input[placeholder*="middle name"]');
      let originalMiddleNamePlaceholder = '';
      if (middleNameInput && !formData.middleName) {
        originalMiddleNamePlaceholder = middleNameInput.placeholder;
        middleNameInput.placeholder = '';
      }
      
      // === BULLETPROOFING: Lock all font sizes to absolute pixels ===
      element.querySelectorAll('*').forEach(el => {
        const computed = window.getComputedStyle(el);
        const fontSize = computed.fontSize;
        // Only modify if it's a text-containing element
        if (fontSize && el.childNodes.length > 0) {
          modifyStyle(el, { fontSize: fontSize }); // Forces computed px value
        }
      });
      
      // Explicitly set base font sizes for common elements
      element.querySelectorAll('label').forEach(el => {
        modifyStyle(el, { fontSize: '14px', lineHeight: '1.5' });
      });
      element.querySelectorAll('input').forEach(el => {
        modifyStyle(el, { fontSize: '14px', lineHeight: '1.5' });
      });
      element.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(el => {
        const computed = window.getComputedStyle(el);
        modifyStyle(el, { fontSize: computed.fontSize, lineHeight: computed.lineHeight });
      });
      
      // Replace textarea with div to preserve line breaks
      element.querySelectorAll('textarea').forEach(ta => {
        const value = ta.value || '';
        const replacementDiv = document.createElement('div');
        replacementDiv.style.cssText = `
          padding: 12px 16px; border: 1px solid #e5e7eb; border-radius: 6px;
          background-color: #f9fafb; font-size: 14px; line-height: 1.6;
          min-height: 100px; white-space: pre-wrap; word-wrap: break-word;
        `;
        replacementDiv.textContent = value;
        replacementDiv.id = 'temp-textarea-replacement';
        ta.style.display = 'none';
        ta.parentNode.insertBefore(replacementDiv, ta.nextSibling);
      });
      
      // Add footer
      const footer = document.createElement('div');
      footer.id = 'temp-pdf-footer';
      footer.style.cssText = 'padding: 20px 16px; margin-top: 20px; opacity: 0;';
      footer.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: flex-end;">
          <div style="display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 1;">
            <div style="font-size: 14px; font-weight: 500; letter-spacing: 0.025em; color: #6b7280;">
              Client Personal Details
            </div>
            <div style="font-size: 14px; font-weight: 500; letter-spacing: 0.025em; color: #6b7280;">
              Document <span style="color: #1f2937; font-weight: 600;">01</span> of <span style="color: #1f2937; font-weight: 600;">07</span>
            </div>
          </div>
          <img src="/images/TCA_Short_Logo.png" alt="TCA" style="height: 48px; opacity: 0.8; transform: translateX(20px);" />
        </div>
      `;
      element.appendChild(footer);
      footer.style.opacity = '1';
      
      // === BULLETPROOFING: Extended stabilization delay ===
      await new Promise(r => setTimeout(r, 300));
      
      // Generate PDF with bulletproofed settings
      const pdfDataUri = await html2pdf()
        .set({
          margin: [2, 2, 2, 2],
          filename: 'Page_01_Client_Personal_Details.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: '#ffffff',
            // === BULLETPROOFING: Fixed dimensions ===
            windowWidth: PDF_RENDER_WIDTH,
            width: PDF_RENDER_WIDTH,
            scrollX: 0,
            scrollY: 0,
            // Apply grid fixes to cloned document
            onclone: (clonedDoc) => {
              const clonedElement = clonedDoc.querySelector('#page-pdf-content');
              if (clonedElement) {
                clonedElement.style.width = `${PDF_RENDER_WIDTH}px`;
                clonedElement.style.maxWidth = `${PDF_RENDER_WIDTH}px`;
                clonedElement.style.minWidth = `${PDF_RENDER_WIDTH}px`;
                clonedElement.style.transform = 'none';
                
                // Force grid columns in cloned document
                clonedElement.querySelectorAll('.grid').forEach(grid => {
                  const classList = grid.className;
                  if (classList.includes('grid-cols-2') || classList.includes('sm:grid-cols-2') || 
                      classList.includes('md:grid-cols-2') || classList.includes('lg:grid-cols-2')) {
                    grid.style.gridTemplateColumns = 'repeat(2, minmax(0, 1fr))';
                  }
                  if (classList.includes('grid-cols-3') || classList.includes('sm:grid-cols-3') || 
                      classList.includes('md:grid-cols-3') || classList.includes('lg:grid-cols-3')) {
                    grid.style.gridTemplateColumns = 'repeat(3, minmax(0, 1fr))';
                  }
                  if (classList.includes('grid-cols-4') || classList.includes('sm:grid-cols-4') || 
                      classList.includes('md:grid-cols-4') || classList.includes('lg:grid-cols-4')) {
                    grid.style.gridTemplateColumns = 'repeat(4, minmax(0, 1fr))';
                  }
                });
              }
            }
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { 
            mode: ['css', 'legacy'],
            after: '.page-break-after-cover'
          }
        })
        .from(element)
        .outputPdf('datauristring');
      
      // === RESTORE ORIGINAL STATE ===
      // Remove bulletproof style tag
      const bulletproofStyles = document.getElementById('pdf-bulletproof-styles');
      if (bulletproofStyles) bulletproofStyles.remove();
      
      // Restore body and element dimensions
      document.body.style.cssText = originalBodyStyle;
      element.style.width = originalElementWidth;
      element.style.maxWidth = originalElementMaxWidth;
      element.style.minWidth = originalElementMinWidth;
      element.style.transform = originalElementTransform;
      element.style.position = originalElementPosition;
      
      const tempFooter = document.getElementById('temp-pdf-footer');
      if (tempFooter) tempFooter.remove();
      element.querySelectorAll('#temp-textarea-replacement').forEach(div => div.remove());
      element.querySelectorAll('textarea').forEach(ta => { ta.style.display = ''; });
      originalStyles.forEach((origStyle, el) => { el.style.cssText = origStyle; });
      
      // Restore work email placeholder
      if (workEmailInput && originalWorkEmailPlaceholder) {
        workEmailInput.placeholder = originalWorkEmailPlaceholder;
      }
      
      // Restore Second Name placeholder
      if (secondNameInput && originalSecondNamePlaceholder) {
        secondNameInput.placeholder = originalSecondNamePlaceholder;
      }
      
      // Restore Middle Name placeholder
      if (middleNameInput && originalMiddleNamePlaceholder) {
        middleNameInput.placeholder = originalMiddleNamePlaceholder;
      }
      
      // Save PDF to backend with retry logic and verification
      const MAX_RETRIES = 3;
      let saveSuccessful = false;
      let lastError = null;
      
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          console.log(`[Page1] Save attempt ${attempt}/${MAX_RETRIES}...`);
          
          // Step 1: Save the PDF
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/save-page`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              form_id: formId,
              page_number: 1,
              pdf_data: pdfDataUri,
              page_title: 'Client Personal Details',
              client_data: { clientType, ...formData, formId }
            })
          });
          
          if (!response.ok) {
            throw new Error(`Server returned ${response.status}`);
          }
          
          // Step 2: Verify the PDF was actually saved
          const verifyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/debug/${formId}`);
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            const page1Saved = verifyData.pages?.some(p => p.page_number === 1);
            if (page1Saved) {
              console.log('[Page1] PDF saved and verified successfully');
              saveSuccessful = true;
              break;
            } else {
              throw new Error('PDF not found after save');
            }
          } else {
            throw new Error('Verification request failed');
          }
          
        } catch (err) {
          lastError = err;
          console.error(`[Page1] Save attempt ${attempt} failed:`, err.message);
          if (attempt < MAX_RETRIES) {
            // Wait 2 seconds before retry
            await new Promise(r => setTimeout(r, 2000));
          }
        }
      }
      
      if (!saveSuccessful) {
        console.error('[Page1] All save attempts failed:', lastError);
        // Remove overlay and show error
        const overlayEl = document.getElementById('pdf-generation-overlay');
        if (overlayEl) overlayEl.remove();
        setIsGeneratingPDF(false);
        alert('Failed to save your progress after multiple attempts. Please check your internet connection and try again.');
        return; // Don't proceed to next page
      }
      
      // Navigation happens here only if save was successful
      // Remove overlay before navigation
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      setIsGeneratingPDF(false);
      
      // Navigate to Page 2 with form data and formId
      navigate('/page-2', { 
        state: { 
          formId,
          clientType,
          ...formData,
          signedAtPage2: previousData.signedAtPage2,
          signaturePage2: previousData.signaturePage2,
          signedAtPage3: previousData.signedAtPage3,
          signaturePage3: previousData.signaturePage3,
          signedAtPage4: previousData.signedAtPage4,
          signaturePage4: previousData.signaturePage4,
          signedAtPage5: previousData.signedAtPage5,
          signaturePage5: previousData.signaturePage5,
          selectedPackage: previousData.selectedPackage,
          selectedUrgency: previousData.selectedUrgency,
          selectedPaymentMethod: previousData.selectedPaymentMethod,
          paymentCompleted: previousData.paymentCompleted,
          debitOrderCompleted: previousData.debitOrderCompleted
        } 
      });
      
    } catch (error) {
      console.error('[Page1] PDF generation error:', error);
      alert('An error occurred while generating your PDF. Please try again.');
      // Remove overlay on error
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      setIsGeneratingPDF(false);
    }
  };

  // Check if physical address has at least 3 non-empty lines
  const isPhysicalAddressValid = () => {
    if (!formData.physicalAddress) return false;
    const lines = formData.physicalAddress.split('\n');
    const nonEmptyLines = lines.filter(line => line.trim().length > 0);
    return nonEmptyLines.length >= 3;
  };

  const isFormValid = () => {
    return (
      clientType &&
      formData.firstName &&
      formData.surname &&
      formData.idNumber &&
      formData.taxNumber &&
      formData.emailAddress &&
      formData.cellphoneNumber &&
      formData.maritalStatus &&
      formData.page1BankName &&
      formData.page1AccountNumber &&
      isPhysicalAddressValid()
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content - wrapped for PDF capture */}
      <main id="page-pdf-content" className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-8 sm:pb-12 bg-white">
        {/* Logo Section - Both logos centered */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center gap-4">
          <img 
            src={LOGO_URL} 
            alt="TCA Auditors Logo" 
            className="h-24 sm:h-32 w-auto object-contain"
            draggable="false"
          />
          {/* Second logo - 40% larger total (20% + 20%) */}
          <img 
            src={BANNER_URL} 
            alt="Tax & Auditing Consultants - For Businesses & Individuals" 
            className="h-[4.5rem] sm:h-24 w-auto object-contain"
          />
        </div>

        {/* Main Heading - TCA Blue with blue underline matching "Please select your client status:" */}
        <div className="mb-8 sm:mb-10 pb-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            TAX SERVICES AGREEMENT
          </h1>
        </div>

        {/* Form Card */}
        <Card className="w-full max-w-2xl border-0 shadow-card">
          <CardContent className="p-6 sm:p-10">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Client Type Selection */}
              <div className="space-y-4">
                {/* Label with TCA blue underline */}
                <div className="pb-2">
                  <Label className="text-lg font-semibold text-tca-blue inline-block underline-tca-blue">
                    Please select your client status:
                  </Label>
                </div>
                <RadioGroup 
                  value={clientType} 
                  onValueChange={setClientType}
                  className="flex flex-col sm:flex-row gap-12 sm:gap-24 pt-3"
                >
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem 
                      value="new" 
                      id="new-client"
                      className={`h-5 w-5 border-2 ${
                        clientType === 'new' 
                          ? 'radio-orange' 
                          : 'border-muted-foreground'
                      }`}
                    />
                    <Label 
                      htmlFor="new-client" 
                      className={`text-base font-medium cursor-pointer transition-all duration-300 ${
                        clientType === 'new' 
                          ? 'gradient-bg-orange font-semibold' 
                          : 'text-foreground'
                      }`}
                    >
                      New Client
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem 
                      value="existing" 
                      id="existing-client"
                      className={`h-5 w-5 border-2 ${
                        clientType === 'existing' 
                          ? 'radio-green' 
                          : 'border-muted-foreground'
                      }`}
                    />
                    <Label 
                      htmlFor="existing-client" 
                      className={`text-base font-medium cursor-pointer transition-all duration-300 ${
                        clientType === 'existing' 
                          ? 'gradient-bg-green font-semibold' 
                          : 'text-foreground'
                      }`}
                    >
                      Existing Client
                    </Label>
                  </div>
                </RadioGroup>
                
                {/* Existing Client Welcome Message */}
                {clientType === 'existing' && (
                  <p className="text-sm italic mt-2" style={{ color: 'hsl(142, 76%, 36%)' }} data-hide-in-pdf="true">
                    <strong>Welcome back!</strong> As a valued client, an exclusive loyalty discount awaits you. Complete the form to unlock your coupon code. The coupon code can be claimed during the payment process.
                  </p>
                )}
              </div>

              {/* Form Fields - Only show when client type is selected */}
              {clientType && (
                <div className="space-y-6 animate-fade-in">
                  {/* Divider */}
                  <div className="border-t border-border" />
                  
                  {/* How did you hear about us - Only for New Clients */}
                  {clientType === 'new' && (
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-foreground">
                        How did you hear about us?
                      </Label>
                      <Select 
                        value={formData.referralSource} 
                        onValueChange={(value) => handleInputChange('referralSource', value)}
                      >
                        <SelectTrigger className="h-11 px-4 bg-background border-input hover:border-muted-foreground/50 focus:ring-2 focus:ring-accent focus:ring-offset-0">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {REFERRAL_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Name Fields Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FormField 
                      label="First Name" 
                      required 
                      value={formData.firstName}
                      onChange={(value) => handleInputChange('firstName', value)}
                      onBlur={() => handleInputBlur('firstName')}
                      placeholder="Enter your first name"
                    />
                    <FormField 
                      label="Second Name" 
                      value={formData.secondName}
                      onChange={(value) => handleInputChange('secondName', value)}
                      onBlur={() => handleInputBlur('secondName')}
                      placeholder="Enter your second name"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FormField 
                      label="Middle Name" 
                      value={formData.middleName}
                      onChange={(value) => handleInputChange('middleName', value)}
                      onBlur={() => handleInputBlur('middleName')}
                      placeholder="Enter your middle name"
                    />
                    <FormField 
                      label="Surname" 
                      required 
                      value={formData.surname}
                      onChange={(value) => handleInputChange('surname', value)}
                      onBlur={() => handleInputBlur('surname')}
                      placeholder="Enter your surname"
                    />
                  </div>

                  {/* ID Number */}
                  <div className="space-y-2">
                    <FormField 
                      label="ID Number" 
                      required 
                      value={formData.idNumber}
                      onChange={(value) => handleInputChange('idNumber', value)}
                      onBlur={handleIdNumberBlur}
                      placeholder="Enter your 13-digit SA ID number"
                      error={fieldErrors.idNumber}
                      showError={touchedFields.idNumber}
                      digitsOnly={true}
                    />
                    {/* ID Number Valid Display */}
                    {idNumberInfo && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                        <p className="font-semibold text-green-800">✓ Valid South African ID Number</p>
                      </div>
                    )}
                  </div>

                  {/* Personal Income Tax Number */}
                  <FormField 
                    label="Personal Income Tax Number" 
                    required 
                    value={formData.taxNumber}
                    onChange={(value) => handleInputChange('taxNumber', value)}
                    onBlur={handleTaxNumberBlur}
                    placeholder="Enter your 10-digit tax number"
                    error={fieldErrors.taxNumber}
                    showError={touchedFields.taxNumber}
                  />

                  {/* Email Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FormField 
                      label="Email Address" 
                      required 
                      type="email"
                      value={formData.emailAddress}
                      onChange={(value) => handleInputChange('emailAddress', value)}
                      onBlur={() => handleEmailBlur('emailAddress')}
                      placeholder="your.email@example.com"
                      error={fieldErrors.emailAddress}
                      showError={touchedFields.emailAddress}
                    />
                    <FormField 
                      label="Work/Alternative Email Address" 
                      type="email"
                      value={formData.workEmail}
                      onChange={(value) => handleInputChange('workEmail', value)}
                      onBlur={() => handleEmailBlur('workEmail')}
                      placeholder="work.email@company.com (optional)"
                      error={fieldErrors.workEmail}
                      showError={touchedFields.workEmail}
                    />
                  </div>

                  {/* Cellphone */}
                  <FormField 
                    label="Cellphone Number" 
                    required 
                    type="tel"
                    value={formData.cellphoneNumber}
                    onChange={(value) => handleInputChange('cellphoneNumber', value)}
                    onBlur={handleCellphoneBlur}
                    placeholder="0XX XXX XXXX"
                    error={fieldErrors.cellphoneNumber}
                    showError={touchedFields.cellphoneNumber}
                  />

                  {/* Marital Status */}
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-foreground">
                      Marital Status<span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.maritalStatus}
                      onValueChange={(value) => handleInputChange('maritalStatus', value)}
                    >
                      <SelectTrigger className="w-full h-11" data-testid="marital-status-select">
                        <SelectValue placeholder="Select marital status..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Not Married" data-testid="marital-status-not-married">Not Married</SelectItem>
                        <SelectItem value="Married Out Of Community" data-testid="marital-status-out-of-community">Married Out Of Community</SelectItem>
                        <SelectItem value="Married In Community" data-testid="marital-status-in-community">Married In Community</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Bank Account Details for Tax Refund */}
                  <div className="space-y-4 p-4 sm:p-5 rounded-lg border border-input bg-muted/30" data-testid="page1-bank-details-section">
                    <div>
                      <Label className="text-sm font-bold text-foreground">
                        Bank Account Details for Tax Refund<span className="text-red-500">*</span>
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        Please provide the bank account into which SARS should pay your tax refund (if applicable).
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-foreground">
                          Bank Name (ABSA, FNB etc.)<span className="text-red-500">*</span>
                        </Label>
                        <Popover open={openBankCombobox} onOpenChange={setOpenBankCombobox}>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              role="combobox"
                              aria-expanded={openBankCombobox}
                              data-testid="page1-bank-select-trigger"
                              className="w-full h-11 justify-between font-normal border-input hover:border-muted-foreground/50"
                            >
                              <span className={formData.page1BankName ? '' : 'text-muted-foreground'}>
                                {formData.page1BankName || 'Select bank...'}
                              </span>
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[300px] p-0">
                            <Command>
                              <CommandInput placeholder="Search bank..." />
                              <CommandList>
                                <CommandEmpty>No bank found.</CommandEmpty>
                                <CommandGroup>
                                  {SOUTH_AFRICAN_BANKS.map((bank) => (
                                    <CommandItem
                                      key={bank.value}
                                      value={bank.label}
                                      data-testid={`page1-bank-option-${bank.value}`}
                                      onSelect={(currentValue) => {
                                        const selectedBank = SOUTH_AFRICAN_BANKS.find(
                                          (b) => b.label.toLowerCase() === currentValue.toLowerCase()
                                        );
                                        setFormData((prev) => ({
                                          ...prev,
                                          page1BankName: currentValue === prev.page1BankName ? '' : currentValue,
                                          page1BranchName: selectedBank ? 'UNIVERSAL BRANCH' : '',
                                          page1BranchCode: selectedBank ? selectedBank.branchCode : '',
                                        }));
                                        if (formData.page1AccountNumber) {
                                          setPage1AccountWarning(validateAccountNumber(formData.page1AccountNumber, currentValue));
                                        }
                                        setOpenBankCombobox(false);
                                      }}
                                    >
                                      <Check className={cn('mr-2 h-4 w-4', formData.page1BankName === bank.label ? 'opacity-100' : 'opacity-0')} />
                                      {bank.label}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-foreground">Branch Name</Label>
                        <input
                          type="text"
                          value={formData.page1BranchName}
                          readOnly
                          data-testid="page1-branch-name"
                          className="w-full h-11 px-4 rounded-md border border-input bg-muted text-sm text-muted-foreground"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-foreground">Universal Bank Branch Code</Label>
                        <input
                          type="text"
                          value={formData.page1BranchCode}
                          readOnly
                          data-testid="page1-branch-code"
                          className="w-full h-11 px-4 rounded-md border border-input bg-muted text-sm text-muted-foreground"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-bold text-foreground">
                          Account Number<span className="text-red-500">*</span>
                        </Label>
                        <input
                          type="text"
                          value={formData.page1AccountNumber}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleInputChange('page1AccountNumber', value);
                            setPage1AccountWarning(validateAccountNumber(value, formData.page1BankName));
                          }}
                          placeholder="Enter account number"
                          data-testid="page1-account-number"
                          className={`w-full h-11 px-4 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-accent hover:border-muted-foreground/50 ${
                            page1AccountWarning ? 'border-amber-400 bg-amber-50' : 'border-input bg-background'
                          }`}
                        />
                        {page1AccountWarning && (
                          <p className="text-xs text-amber-600" data-testid="page1-account-warning">{page1AccountWarning}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-foreground">
                        Type of Account<span className="text-red-500">*</span>
                      </Label>
                      <div className="flex flex-wrap gap-4">
                        {['Current', 'Savings', 'Transmission'].map((type) => (
                          <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name="page1AccountType"
                              value={type}
                              checked={formData.page1AccountType === type}
                              onChange={() => handleInputChange('page1AccountType', type)}
                              data-testid={`page1-account-type-${type.toLowerCase()}`}
                            />
                            <span>{type === 'Current' ? 'Current (aka Cheque)' : type}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Physical Address */}
                  <div className="space-y-2 relative">
                    {/* Tooltip positioned above the label */}
                    {showAddressTip && formData.physicalAddress && (
                      <div className="absolute left-0 right-0 -top-2 transform -translate-y-full z-10 bg-blue-50 border border-blue-200 rounded-lg p-3 shadow-lg">
                        <div className="flex items-start gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="text-xs text-blue-800">
                            <p className="font-semibold mb-1">Address line guidance:</p>
                            <p>Line 1: Unit number (e.g. Unit 01)</p>
                            <p>Line 2: Estate/Complex name (e.g. Steyn City Estate)</p>
                            <p>Line 3: Street address (e.g. 33 William Nicol Road)</p>
                            <p>Line 4: Suburb/City (e.g. Sandton)</p>
                            <p>Line 5: Postal code (e.g. 2196)</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <Label className="text-sm font-bold text-foreground">
                      Physical Address (as at today)<span className="text-red-500">*</span>
                    </Label>
                    <textarea
                      value={formData.physicalAddress}
                      onChange={(e) => {
                        const value = e.target.value;
                        const lines = value.split('\n');
                        // Limit to 5 lines maximum
                        if (lines.length <= 5) {
                          handleInputChange('physicalAddress', value);
                        } else {
                          // Keep only the first 5 lines
                          handleInputChange('physicalAddress', lines.slice(0, 5).join('\n'));
                        }
                      }}
                      onKeyDown={(e) => {
                        // Prevent Enter key if already at 5 lines
                        if (e.key === 'Enter') {
                          const lines = formData.physicalAddress.split('\n');
                          if (lines.length >= 5) {
                            e.preventDefault();
                          }
                        }
                      }}
                      onFocus={() => setShowAddressTip(true)}
                      onBlur={() => setShowAddressTip(false)}
                      placeholder={"Physical address line 01 (e.g. Unit 01)\nPhysical address line 02 (e.g. Steyn City Estate)\nPhysical address line 03 (e.g. 33 William Nicol Road)\nPhysical address line 04 (e.g. Sandton)\nPhysical address line 05 (e.g. 2196)"}
                      rows={5}
                      className="w-full px-4 py-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-0 hover:border-muted-foreground/50 resize-none"
                    />
                  </div>

                  {/* Submit Button - Hidden in PDF */}
                  <div className="pt-4 flex flex-col sm:flex-row gap-3 items-center" data-hide-in-pdf="true">
                    <a 
                      href="https://tax.tcauditors.co.za"
                      className="inline-flex items-center justify-center text-xs font-bold px-4 py-2 h-9 rounded-md bg-slate-600 hover:bg-slate-700 text-white transition-all duration-200 shadow-sm"
                      data-hide-in-pdf="true"
                    >
                      Start Over
                    </a>
                    <Button 
                      type="submit"
                      disabled={!isFormValid() || isGeneratingPDF}
                      className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGeneratingPDF ? 'Saving...' : 'Go to Next Page...'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={1} totalPages={7} pageTitle="Client Personal Details" />
        <img 
          src={TCA_SHORT_LOGO} 
          alt="TCA Logo" 
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>

      {/* Settings Button - Bottom Right, hidden in PDF */}
      <div className="flex justify-end px-4 pb-4" data-hide-in-pdf="true">
        <button
          type="button"
          onClick={() => navigate('/admin/login')}
          className="inline-flex items-center justify-center gap-1.5 text-xs font-bold px-4 py-2 h-9 rounded-md bg-slate-600 hover:bg-slate-700 text-white transition-all duration-200 shadow-sm"
          data-testid="settings-button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          Settings
        </button>
      </div>

      {/* Error Modal */}
      <Dialog open={errorModal.open} onOpenChange={(open) => setErrorModal({ ...errorModal, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Required Field Missing
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              {errorModal.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={() => setErrorModal({ open: false, message: '' })}
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              OK, I'll fix it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageOne;
